'use client';

/**
 * Auto-opens the post org-creation setup wizard (mounted in LayoutWrapper).
 *
 * Shows when: organization loaded · industry or region section missing in
 * setup_wizard_progress · not a demo org · user can edit the organization
 * (owner/admin) · not dismissed this session. Existing orgs are backfilled
 * as completed by the migration, so only newly created orgs qualify.
 *
 * The sections to ask are frozen at open time: each step save invalidates
 * the organization query, and the refreshed progress must not reset or close
 * the wizard mid-flow. Closing ("Skip for now" / X / Finish) is session-only
 * dismissal via sessionStorage (ephemeral flag — not data caching).
 */

import { useState } from 'react';
import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { useEntitlements } from '@/lib/platform/hooks/use-entitlements';
import { usePermissionsQuery } from '@/lib/platform/hooks/queries/use-permissions-query';
import { canEditOrganisation } from '@/lib/platform/utils/permissions-helpers';
import SetupWizardModal from './SetupWizardModal';

export default function SetupWizardController() {
  const { organization } = useOrganization();
  const { isDemo, isLoading: entitlementsLoading } = useEntitlements();
  const { data: permissions } = usePermissionsQuery();

  const [open, setOpen] = useState(false);
  /** Sections frozen at open time (see module comment) */
  const [ask, setAsk] = useState<{ industry: boolean; region: boolean } | null>(null);

  // Auto-open via adjust-state-during-render (not an effect — see
  // https://react.dev/learn/you-might-not-need-an-effect). The mid-flow org
  // refetch after a step save flips the missing flags, but `open` stays true
  // and `ask` stays frozen, so the wizard never resets or closes itself.
  const progress = organization?.setup_wizard_progress;
  const missingIndustry = !!organization && !progress?.industry;
  const missingRegion = !!organization && !progress?.region;
  const shouldShow =
    !!organization &&
    !entitlementsLoading &&
    !isDemo &&
    canEditOrganisation(permissions) &&
    (missingIndustry || missingRegion) &&
    typeof window !== 'undefined' &&
    sessionStorage.getItem(`setupWizardDismissed:${organization.id}`) !== 'true';

  if (shouldShow && !open) {
    setAsk({ industry: missingIndustry, region: missingRegion });
    setOpen(true);
  }

  const handleClose = () => {
    if (organization) {
      sessionStorage.setItem(`setupWizardDismissed:${organization.id}`, 'true');
    }
    setOpen(false);
  };

  if (!organization || !ask) return null;

  return (
    <SetupWizardModal
      key={organization.id} // reset wizard state when switching organization
      isOpen={open}
      onClose={handleClose}
      organizationId={organization.id}
      initialCountryCode={organization.country_code}
      askIndustry={ask.industry}
      askRegion={ask.region}
    />
  );
}
